const yuan = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });

export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `¥${yuan.format(value)}`;
}

export function count(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value >= 10_000 ? compact.format(value) : String(value);
}

export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function floatValue(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toFixed(8);
}

/** Nome sem o prefixo StatTrak™, que já é exibido como badge separado. */
export function cleanName(name: string | null | undefined): string {
  if (!name) return '—';
  return name.replace(/^StatTrak™\s*/i, '').replace(/^Souvenir\s*/i, '');
}

export function dateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

/** Verde quando o Youpin está mais barato que a Steam. */
export function discountClass(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value < 0 ? 'pos' : 'neg';
}

/**
 * Separa "AK-47 | Redline (Field-Tested)" em arma e skin.
 *
 * O card do upgrade mostra as duas linhas com pesos diferentes — a arma é o
 * contexto, a skin é o que o cliente procura. Vindo do inventário da Steam o
 * nome chega inteiro, com StatTrak™ e desgaste grudados: os dois já aparecem
 * como selo próprio no card e sairiam repetidos no texto.
 */
export function dividirNome(name: string | null | undefined): { arma: string; skin: string } {
  const limpo = cleanName(name).replace(/\s*\([^)]*\)\s*$/, '').trim();
  const corte = limpo.indexOf('|');
  if (corte < 0) return { arma: '', skin: limpo };
  return { arma: limpo.slice(0, corte).trim(), skin: limpo.slice(corte + 1).trim() };
}

const DESGASTES: { code: string; termos: string[] }[] = [
  { code: 'FN', termos: ['factory new', 'nova de fábrica', 'nova de fabrica'] },
  { code: 'MW', termos: ['minimal wear', 'pouco usada'] },
  { code: 'FT', termos: ['field-tested', 'field tested', 'testada em campo'] },
  { code: 'WW', termos: ['well-worn', 'well worn', 'bem desgastada'] },
  { code: 'BS', termos: ['battle-scarred', 'battle scarred', 'veterana de guerra'] },
];

/**
 * Sigla do desgaste (FN, MW, FT, WW, BS) a partir de qualquer texto.
 *
 * O inventário da Steam devolve o desgaste como rótulo traduzido e o catálogo
 * como código; o selo do card precisa dos dois caminhos.
 */
export function codigoDesgaste(...textos: (string | null | undefined)[]): string | null {
  for (const texto of textos) {
    if (!texto) continue;
    const t = texto.toLowerCase();
    if (/^(fn|mw|ft|ww|bs)$/.test(t)) return t.toUpperCase();
    const achado = DESGASTES.find((d) => d.termos.some((termo) => t.includes(termo)));
    if (achado) return achado.code;
  }
  return null;
}

/**
 * Quebra um preço já formatado em parte inteira e centavos.
 *
 * Os centavos vão menores no card, para o olho bater no valor cheio primeiro.
 * Trabalhar sobre o texto pronto — e não sobre o número — mantém o card alheio
 * à moeda: reais do inventário e yuans do catálogo passam pelo mesmo caminho.
 */
export function partesPreco(texto: string): [string, string] {
  const m = /^(.*)([.,]\d{2})$/.exec(texto);
  return m ? [m[1]!, m[2]!] : [texto, ''];
}
